import { AbstractBuildstep, D90 } from "@project/emerge";
import type { AbstractPartDefinition } from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import find from "lodash/find";
import * as THREE from "three";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { PanelType } from "../enum/PanelType";
import { LTPartType } from "../enum/PartType";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PanelColor } from "../interfaces/PanelColor";
import type { RollerBoardInfos } from "../interfaces/RollerBoardInfos";
import type {
  CreeperRollerType,
  EdgeRollerType,
  RollerBoardProperties,
  RollerType,
} from "../interfaces/RollerBoardProperties";
import type { RollerPanelProperties } from "../interfaces/RollerPanelProperties";
import type { RollerDefinition } from "../interfaces/RollerProperties";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import { getHydratedLegacyCreeperWheel } from "../settings/hydrateImportedCreeperRollers";
import { DefaultGroupName } from "../structure/Rect";
import { RectSizeHelper } from "../structure/RectSizeHelper";
import { OptionDBInstance } from "../user_options/options_db";

// --- Dependency Data Interfaces ---

interface BaseRollerStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string;
}

interface RollerPanelDependencyData extends BaseRollerStepDependency {
  partKind: "RollerPanel";
  type: LTPartType.Panel;
  panelType: PanelType.RollerBoard;
  material: TrayMaterialType;
  color: PanelColor;
  strength: number;
  note: string;
  braked: boolean; // From settings.exteriorOptions.rollers.board.braked
  size: THREE.Vector2;
  position: THREE.Vector3;
  side: Sides.BOTTOM;
  n: number; // Index for multiple panels
}

interface RollerDependencyData extends BaseRollerStepDependency {
  partKind: "Roller";
  type: LTPartType.Roller;
  partSelection: RollerType | CreeperRollerType; // Standard or Creeper roller config
  position: THREE.Vector3;
  side: Sides; // Widened type from Sides.BOTTOM | Sides.LEFT | Sides.RIGHT
  rotation?: THREE.Euler; // Optional: Pre-calculated for edge rollers
  isSupplyPart: true;
}

type RollerStepDependencyData =
  | RollerPanelDependencyData
  | RollerDependencyData;

// --- Refactored Build Step ---

export class RollerBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AbstractPartDefinition<string>,
  RollerStepDependencyData // Dependency data union type
> {
  constructor() {
    super("RollerBuildStep");
  }

  // Helper: get base rect and size helper
  private getBaseRectAndSizeHelper(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ) {
    const baseRect = find(blueprint.rects, {
      side: Sides.BOTTOM,
      group: DefaultGroupName,
    });

    if (!baseRect) {
      console.warn(
        "RollerBuildStep: Base bottom rect not found. Skipping roller generation.",
      );
      return null;
    }

    const sizeHelper = new RectSizeHelper(baseRect, false);
    const { profiles, strength } = settings.exteriorOptions;

    for (const edge of [EdgeIndex.BOTTOM, EdgeIndex.TOP]) {
      if (baseRect.edges[edge]?.type === EdgeType.FROMCUT) {
        sizeHelper.expand(edge, 40 - profiles.extra / 2);
      } else if (baseRect.edges[(edge + 2) % 4]?.type === EdgeType.FROMCUT) {
        sizeHelper.shrink((edge + 2) % 4, strength);
      }
    }

    return { baseRect, sizeHelper };
  }

  // Helper: create panel material
  private createPanelMaterial(
    board: RollerBoardProperties,
    rollers: CreeperRollerType,
  ): TrayMaterialType {
    return {
      description: rollers.description ?? rollers.name,
      name: rollers.name,
      strength: board.strength,
      kind: LTCaseUserOption.TrayMaterialType,
      deleted: false,
    };
  }

  // Helper: create panel dependency
  private createPanelDependency(
    panelId: string,
    panelMaterial: TrayMaterialType,
    holzbraunColor: PanelColor,
    board: RollerBoardProperties,
    panelSize: THREE.Vector2,
    panelPosition: THREE.Vector3,
    n: number,
  ): RollerPanelDependencyData {
    return {
      partId: panelId,
      partKind: "RollerPanel",
      type: LTPartType.Panel,
      panelType: PanelType.RollerBoard,
      groupName: DefaultGroupName,
      buildstepName: this.buildstepName,
      material: panelMaterial,
      color: holzbraunColor,
      strength: panelMaterial.strength,
      note: "Rollbrett",
      braked: board.braked,
      size: panelSize,
      position: panelPosition,
      side: Sides.BOTTOM,
      n,
    };
  }

  // Helper: create roller dependency
  private createRollerDependency(
    rollerId: string,
    roller: CreeperRollerType | EdgeRollerType,
    rollerPosition: THREE.Vector3,
    side: Sides = Sides.BOTTOM,
    rotation?: THREE.Euler,
  ): RollerDependencyData {
    // Rollers now always have a single 'default' mesh
    return {
      partId: rollerId,
      partKind: "Roller",
      type: LTPartType.Roller,
      groupName: DefaultGroupName,
      buildstepName: this.buildstepName,
      partSelection: roller,
      position: rollerPosition,
      side,
      rotation,
      isSupplyPart: true,
    };
  }

  private getRollerBoardOptions(
    settings: FlightCaseSettings,
  ): RollerBoardInfos[] {
    return settings.variableOptions.filter(
      (option): option is RollerBoardInfos =>
        option.variableOptionType === VariableOptionType.ROLLER_BOARD,
    );
  }

  // Temporary fallback so existing cases with exterior rollbrett keep working.
  private createLegacyRollerBoardOption(
    settings: FlightCaseSettings,
  ): RollerBoardInfos | null {
    const legacyRoller = settings.exteriorOptions.rollers;
    if (legacyRoller.rollerType !== ROLLER_TYPE.CREEPER) {
      return null;
    }

    const creeperRoller = legacyRoller as CreeperRollerType;
    // Bundle exterior data is hydrated in FlightCaseDefinition (per-wheel catalog rows on __hydratedWheelA/B).
    const legacyRollerDistanceFromEdge =
      (creeperRoller as { distanceFromEdge?: number }).distanceFromEdge ?? 27;
    return {
      layoutMode: "AUTO",
      rollerA: getHydratedLegacyCreeperWheel(creeperRoller, "A"),
      rollerB: getHydratedLegacyCreeperWheel(creeperRoller, "B"),
      singlePanelDepthInsetMm: 12,
      singlePanelSideInsetMm: 62,
      splitThresholdMm: 650,
      rollerDistanceFromEdge: legacyRollerDistanceFromEdge,
      uuid: "legacy_rollbrett",
      variableOptionType: VariableOptionType.ROLLER_BOARD,
    };
  }

  private shouldUseSplitPanels(
    width: number,
    depth: number,
    option: RollerBoardInfos,
  ): boolean {
    switch (option.layoutMode) {
      case "SPLIT_PANEL":
        return true;
      case "SINGLE_PANEL":
        return false;
      default: {
        const threshold =
          Number.isFinite(option.splitThresholdMm) &&
          option.splitThresholdMm > 0
            ? option.splitThresholdMm
            : 650;
        return width >= threshold || depth >= threshold;
      }
    }
  }

  private getRollerBoardIdPrefix(
    option: RollerBoardInfos,
    index: number,
  ): string {
    const source = option.uuid || `roller_board_${index}`;
    return source.replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  private resolveRoller(roller: CreeperRollerType): CreeperRollerType {
    if (roller.board) return roller;
    const name = roller.name?.replace(/\s/g, "");
    if (!name) return roller;
    const all =
      OptionDBInstance.getAllOfKind<RollerType>(LTCaseUserOption.RollerType) ??
      [];
    const match = all.find(
      (r): r is CreeperRollerType =>
        r.rollerType === ROLLER_TYPE.CREEPER &&
        r.name?.replace(/\s/g, "") === name,
    );
    return match ?? roller;
  }

  // Helper: handle creeper type rollers
  private handleCreeperType(
    blueprint: FlightCaseBlueprint,
    _settings: FlightCaseSettings,
    sizeHelper: RectSizeHelper,
    option: RollerBoardInfos,
    idPrefix: string,
    dependencies: Map<string, RollerStepDependencyData>,
  ) {
    const width = sizeHelper.size.x;
    const depth = sizeHelper.size.y;
    const rollerA = this.resolveRoller(option.rollerA);
    const rollerB = this.resolveRoller(option.rollerB);
    const board = rollerA.board;
    const smallerDim =
      blueprint.outerDimensions.x > blueprint.outerDimensions.z ? "z" : "x";
    const boardWidth = blueprint.outerDimensions[smallerDim];

    const panelMaterial = this.createPanelMaterial(board, rollerA);
    const holzbraunColor = OptionDBInstance.getOrThrow<PanelColor>(
      LTCaseUserOption.PanelColor,
      { name: "schokobraun (RAL8017)" },
    );

    if (this.shouldUseSplitPanels(width, depth, option)) {
      this.handleDoublePanels({
        idPrefix,
        blueprint,
        board,
        rollerA,
        rollerB,
        boardWidth,
        width,
        depth,
        panelMaterial,
        holzbraunColor,
        dependencies,
        option,
      });
    } else {
      this.handleSinglePanel({
        idPrefix,
        option,
        blueprint,
        board,
        rollerA,
        rollerB,
        width,
        depth,
        sizeHelper,
        panelMaterial,
        holzbraunColor,
        dependencies,
      });
    }
  }

  // Helper: create panel for double panels
  private createDoublePanel(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    boardWidth: number;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    p2: number;
    n: number;
    panelCounter: number;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      boardWidth,
      panelMaterial,
      holzbraunColor,
      p2,
      n,
      panelCounter,
    } = args;
    const panelId = `${idPrefix}_rollerpanel_double_${n}_${panelCounter}`;
    const isWide = blueprint.innerDimensions.x > blueprint.innerDimensions.z;
    const panelPosition = isWide
      ? new THREE.Vector3(
          p2,
          -blueprint.outerDimensions.y / 2 - board.strength / 2,
          0,
        )
      : new THREE.Vector3(
          0,
          -blueprint.outerDimensions.y / 2 - board.strength / 2,
          p2,
        );
    // size.x → world X, size.y → world Z for BOTTOM in SupplyPartPanelInteraction bbox.
    // Rollers sit along Z when isWide (posA/posB on Z), along X when narrow — match half-spans.
    const panelSize = isWide
      ? new THREE.Vector2(board.width, boardWidth)
      : new THREE.Vector2(boardWidth, board.width);

    return this.createPanelDependency(
      panelId,
      panelMaterial,
      holzbraunColor,
      board,
      panelSize,
      panelPosition,
      n,
    );
  }

  // Helper: create rollers for double panels
  private createDoublePanelRollers(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    posA: number;
    posB: number;
    p2: number;
    n: number;
    rollerCounter: number;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      rollerA,
      rollerB,
      posA,
      posB,
      p2,
      n,
      rollerCounter,
    } = args;
    const results: RollerDependencyData[] = [];

    const isWide = blueprint.innerDimensions.x > blueprint.innerDimensions.z;

    // Create roller A
    const rollerAId = `${idPrefix}_roller_double_${n - 1}_A_${rollerCounter}`;
    const rollerAPosition = isWide
      ? new THREE.Vector3(
          p2,
          -blueprint.outerDimensions.y / 2 - board.strength,
          posA,
        )
      : new THREE.Vector3(
          posA,
          -blueprint.outerDimensions.y / 2 - board.strength,
          p2,
        );

    results.push(
      this.createRollerDependency(
        rollerAId,
        rollerA,
        rollerAPosition,
        Sides.BOTTOM,
      ),
    );

    // Create roller B
    const rollerBId = `${idPrefix}_roller_double_${n - 1}_B_${rollerCounter}`;
    const rollerBPosition = isWide
      ? new THREE.Vector3(
          p2,
          -blueprint.outerDimensions.y / 2 - board.strength,
          posB,
        )
      : new THREE.Vector3(
          posB,
          -blueprint.outerDimensions.y / 2 - board.strength,
          p2,
        );

    results.push(
      this.createRollerDependency(
        rollerBId,
        rollerB,
        rollerBPosition,
        Sides.BOTTOM,
      ),
    );

    return results;
  }

  // Helper: handle double panels
  private handleDoublePanels(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    boardWidth: number;
    width: number;
    depth: number;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    dependencies: Map<string, RollerStepDependencyData>;
    option: RollerBoardInfos;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      rollerA,
      rollerB,
      boardWidth,
      width,
      depth,
      panelMaterial,
      holzbraunColor,
      dependencies,
      option,
    } = args;

    // Match legacy RollerBuilder: long-axis half-offset from bottom-rect sizeHelper, not max(inner).
    const pos =
      (blueprint.innerDimensions.x > blueprint.innerDimensions.z
        ? width
        : depth) /
        2 -
      board.distanceFromEdge -
      board.width / 2;

    // Extract roller properties from the roller options
    // Width belongs to individual rollers, distance from edge belongs to the roller board
    // Width can be stored directly on the CreeperRollerType or in nested rollerA/rollerB properties
    const rollerAWidth = rollerA.width ?? rollerA.rollerA?.width;
    const rollerBWidth = rollerB.width ?? rollerB.rollerB?.width;
    const rollerDistanceFromEdge = option.rollerDistanceFromEdge;

    if (
      rollerAWidth === undefined ||
      rollerBWidth === undefined ||
      rollerDistanceFromEdge === undefined
    ) {
      console.warn(
        "RollerBuildStep: Missing roller width or distanceFromEdge. Skipping double panel rollers.",
      );
      return;
    }

    const posA = -(boardWidth / 2 - rollerDistanceFromEdge - rollerAWidth / 2);
    const posB = boardWidth / 2 - rollerDistanceFromEdge - rollerBWidth / 2;

    let panelCounter = 0;
    let rollerCounter = 0;
    let n = 0;

    for (const p2 of [-pos, pos]) {
      const panelDep = this.createDoublePanel({
        idPrefix,
        blueprint,
        board,
        boardWidth,
        panelMaterial,
        holzbraunColor,
        p2,
        n,
        panelCounter: panelCounter,
      });
      panelCounter++;
      dependencies.set(panelDep.partId, panelDep);
      n++;

      const createdRollers = this.createDoublePanelRollers({
        idPrefix,
        blueprint,
        board,
        rollerA,
        rollerB,
        posA,
        posB,
        p2,
        n,
        rollerCounter,
      });
      for (const rollerDep of createdRollers) {
        dependencies.set(rollerDep.partId, rollerDep);
        rollerCounter++;
      }
    }
  }

  // Helper: create single panel with 4 rollers
  private handleSinglePanel(args: {
    idPrefix: string;
    option: RollerBoardInfos;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    width: number;
    depth: number;
    sizeHelper: RectSizeHelper;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    dependencies: Map<string, RollerStepDependencyData>;
  }) {
    const {
      idPrefix,
      option,
      blueprint,
      board,
      rollerA,
      rollerB,
      width,
      depth,
      sizeHelper,
      panelMaterial,
      holzbraunColor,
      dependencies,
    } = args;
    let panelCounter = 0;
    let rollerCounter = 0;

    const panelId = `${idPrefix}_rollerpanel_single_${panelCounter++}`;
    // Legacy RollerBuilder: center single board on bottom rect (pos3d), not case origin.
    const planZ = sizeHelper.pos3d.z;
    const panelPosition = new THREE.Vector3(
      0,
      -blueprint.outerDimensions.y / 2 - board.strength / 2,
      planZ,
    );
    // Same as double panel: bbox uses size.x on X, size.y on Z; rollers span depth on Z.
    const panelSize = new THREE.Vector2(width, depth);

    const panelDep = this.createPanelDependency(
      panelId,
      panelMaterial,
      holzbraunColor,
      board,
      panelSize,
      panelPosition,
      0,
    );
    dependencies.set(panelId, panelDep);

    // Extract roller properties from the roller options
    // Width belongs to individual rollers
    // Width can be stored directly on the CreeperRollerType or in nested rollerA property
    const rollerAWidth = rollerA.width ?? rollerA.rollerA?.width;

    if (rollerAWidth === undefined) {
      console.warn(
        "RollerBuildStep: Missing rollerA width. Skipping single panel rollers.",
      );
      return;
    }

    // Logic ported from Old Code:
    // It uses specific offsets (12mm and 62mm) and calculates based on rollerA width.
    // It places one 'A' and one 'B' roller for each side (Left/Right).

    for (const sign of [-1, 1]) {
      // Old code calculations:
      // Note: Uses rollerAWidth for alignment of both rollers
      const posA_Z = -(
        depth / 2 -
        rollerAWidth / 2 -
        option.singlePanelDepthInsetMm
      );
      const posB_Z =
        depth / 2 - rollerAWidth / 2 - option.singlePanelDepthInsetMm;
      const posX =
        sign * (width / 2 - rollerAWidth / 2 - option.singlePanelSideInsetMm);

      // --- Roller A ---
      const rollerIdA = `${idPrefix}_roller_single_A_${sign === -1 ? "L" : "R"}_${rollerCounter++}`;
      const positionA = new THREE.Vector3(
        posX,
        -blueprint.outerDimensions.y / 2 - board.strength,
        posA_Z + planZ,
      );

      dependencies.set(
        rollerIdA,
        this.createRollerDependency(
          rollerIdA,
          rollerA,
          positionA,
          Sides.BOTTOM,
        ),
      );

      // --- Roller B ---
      const rollerIdB = `${idPrefix}_roller_single_B_${sign === -1 ? "L" : "R"}_${rollerCounter++}`;
      const positionB = new THREE.Vector3(
        posX,
        -blueprint.outerDimensions.y / 2 - board.strength,
        posB_Z + planZ,
      );

      dependencies.set(
        rollerIdB,
        this.createRollerDependency(
          rollerIdB,
          rollerB,
          positionB,
          Sides.BOTTOM,
        ),
      );
    }
  }

  // Helper: safe numeric value for position calculation (avoids NaN from undefined/serialized data)
  private static safeDim(
    value: number | undefined,
    fallback: number,
    label: string,
    debug: { invalid: string[] },
  ): number {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      debug.invalid.push(`${label}=${String(value)}`);
      return fallback;
    }
    return n;
  }

  // Helper: handle edge type rollers
  private handleEdgeType(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    dependencies: Map<string, RollerStepDependencyData>,
  ) {
    const rollers = settings.exteriorOptions.rollers as EdgeRollerType;
    const { side } = rollers;
    // Sides.LEFT is 0 — must not use `!side` (order 7723, Kantenrolle links).
    if (side !== Sides.LEFT && side !== Sides.RIGHT) {
      console.warn(
        "RollerBuildStep: ROLLER_TYPE.EDGE requires side LEFT or RIGHT. Skipping.",
      );
      return;
    }

    const debug = { invalid: [] as string[] };
    const strength = RollerBuildStep.safeDim(
      settings.exteriorOptions?.strength,
      6.5,
      "exteriorOptions.strength",
      debug,
    );
    const innerX = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.x,
      0,
      "innerDimensions.x",
      debug,
    );
    const innerY = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.y,
      0,
      "innerDimensions.y",
      debug,
    );
    const innerZ = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.z,
      0,
      "innerDimensions.z",
      debug,
    );
    if (debug.invalid.length > 0) {
      console.warn(
        "RollerBuildStep.handleEdgeType: invalid values (using fallbacks):",
        debug.invalid.join(", "),
        {
          innerDimensions: blueprint.innerDimensions,
          strength: settings.exteriorOptions?.strength,
        },
      );
    }

    let rollerCounter = 0;
    // Golden huge-batch: Kantenrollen (EDGE) always use negative world X for both
    // exterior `side` LEFT and RIGHT (7723 links, 10202 rechts variant).
    const xSign = -1;

    for (let i = 0; i < 2; i++) {
      const z = i === 0 ? -1 : 1;
      const rollerId = `roller_edge_${side === Sides.RIGHT ? "R" : "L"}_${i}_${rollerCounter++}`;
      const position = new THREE.Vector3(
        xSign * (innerX / 2 + strength + 1.5),
        -innerY / 2 - strength - 1.5,
        z * (innerZ / 2 + strength - 70),
      );
      const rotation =
        side === Sides.RIGHT
          ? new THREE.Euler(-D90, 0, D90)
          : new THREE.Euler(-D90, 0, -D90);

      const rollerDep = this.createRollerDependency(
        rollerId,
        rollers,
        position,
        side,
        rotation,
      );
      dependencies.set(rollerId, rollerDep);
    }
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, RollerStepDependencyData> {
    const dependencies = new Map<string, RollerStepDependencyData>();

    const baseData = this.getBaseRectAndSizeHelper(blueprint, settings);
    if (!baseData) {
      return dependencies;
    }

    const rollerBoardOptions = this.getRollerBoardOptions(settings);
    if (rollerBoardOptions.length > 0) {
      rollerBoardOptions.forEach((option, index) => {
        this.handleCreeperType(
          blueprint,
          settings,
          baseData.sizeHelper,
          option,
          this.getRollerBoardIdPrefix(option, index),
          dependencies,
        );
      });
    } else {
      const legacyOption = this.createLegacyRollerBoardOption(settings);
      if (legacyOption) {
        this.handleCreeperType(
          blueprint,
          settings,
          baseData.sizeHelper,
          legacyOption,
          "legacy_rollbrett",
          dependencies,
        );
      }
    }

    const { rollers } = settings.exteriorOptions;
    switch (rollers.rollerType) {
      case ROLLER_TYPE.NONE:
      case ROLLER_TYPE.CREEPER:
      case ROLLER_TYPE.CORNER_INBOARD:
      case ROLLER_TYPE.CORNER_OUTBOARD:
        break;
      case ROLLER_TYPE.EDGE:
        this.handleEdgeType(blueprint, settings, dependencies);
        break;
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: RollerStepDependencyData,
  ): RollerDefinition | RollerPanelProperties {
    switch (dependencies.partKind) {
      case "RollerPanel": {
        // Rotation for roller panels is constant
        const finalRotation = this.rotateOntoSide(Sides.BOTTOM, 0);
        return {
          ...dependencies, // Includes type, panelType, material, color, strength, note, braked, size, position, side, n
          id: partId,
          group: dependencies.groupName, // Add group explicitly
          buildstepName: dependencies.buildstepName, // Add buildstepName explicitly
          cutouts: [], // Initialize cutouts
          orientation: 0, // size order matches world XZ footprint (no extra Z-rot vs bbox)
          rotation: finalRotation,
        };
      }
      case "Roller": {
        // Rotation is pre-calculated for EDGE type, defaults for CREEPER type
        const finalRotation = dependencies.rotation
          ? dependencies.rotation.clone()
          : this.rotateOntoSide(Sides.BOTTOM, 0);
        return {
          id: partId,
          ...dependencies, // Includes type, partSelection, position, side, isSupplyPart
          group: dependencies.groupName, // Add group explicitly
          buildstepName: dependencies.buildstepName, // Add buildstepName explicitly
          orientation: 90, // fixiate orientation to 90°
          rotation: finalRotation,
        } as RollerDefinition;
      }
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown part kind: ${exhaustiveCheck}`);
      }
    }
  }
}
